/**
 * Terms of Service — globally acceptable.
 * Arbitration, limitation of liability, IP, acceptable use, eligibility,
 * Indian IT Act intermediary rules, etc.
 */
import { LegalLayout, Section, List } from "./LegalLayout";

export default function TermsOfService() {
  return (
    <LegalLayout
      title="Terms of Service"
      effectiveDate="5 February 2026"
      testid="terms-of-service-page"
    >
      <p>
        These Terms of Service (&ldquo;Terms&rdquo;) govern your access to and use of the
        FitFrik mobile application, web application, and related services operated by
        FitFrik Technologies (&ldquo;FitFrik&rdquo;, &ldquo;we&rdquo;, &ldquo;our&rdquo;,
        &ldquo;us&rdquo;). By creating an account or using the Services in any way, you
        agree to be bound by these Terms and by our Privacy Policy.
      </p>
      <p>
        If you are using the Services on behalf of an organisation, you represent that
        you have authority to bind that organisation to these Terms. If you do not agree,
        you must not use the Services.
      </p>

      <Section n="1" title="Eligibility">
        <List
          items={[
            "You must be at least 16 years old (or the age of digital consent in your jurisdiction) to create an account.",
            "If you are under 18, a parent or legal guardian must review these Terms with you.",
            "You must provide accurate information and keep it up to date.",
            "You may not use the Services if you are restricted from doing so under applicable laws (including export control or sanctions regulations).",
          ]}
        />
      </Section>

      <Section n="2" title="Your account">
        <List
          items={[
            "You are responsible for maintaining the confidentiality of your password and for all activity under your account.",
            "You must notify us immediately at support@fitfrik.com if you suspect unauthorised access to your account.",
            "We may suspend or terminate accounts that violate these Terms, our Acceptable Use Policy, or applicable law.",
          ]}
        />
      </Section>

      <Section n="3" title="Health & fitness disclaimer">
        <p>
          FitFrik provides information, AI-generated suggestions, workouts, meal
          guidance, and body composition estimates for general fitness and wellness
          purposes only. The Services are <strong>not</strong> a substitute for
          professional medical advice, diagnosis, or treatment.
        </p>
        <List
          items={[
            "Consult a qualified physician before starting any exercise or nutrition program, especially if you are pregnant, nursing, under 18, over 60, or have any pre-existing medical condition.",
            "Stop exercising immediately and seek medical attention if you experience pain, dizziness, chest tightness, or any other warning sign.",
            "AI-generated workouts, meal analyses, and body composition estimates are probabilistic and may be inaccurate. You are responsible for your own safety.",
          ]}
        />
      </Section>

      <Section n="4" title="Subscription & payments">
        <List
          items={[
            "Premium tiers (Pro / Elite) are sold as recurring monthly or annual subscriptions via Razorpay.",
            "Prices are displayed in Indian Rupees (INR) inclusive of applicable GST where required.",
            "Subscriptions renew automatically unless cancelled at least 24 hours before the next billing date. Cancel any time in Settings → Subscription.",
            "Failed renewals downgrade the account to Free tier after a 3-day grace period.",
            "Refunds are governed by our Refund Policy, which forms part of these Terms.",
          ]}
        />
      </Section>

      <Section n="5" title="Trainer & nutritionist marketplace">
        <p>
          FitFrik operates a marketplace that connects members with independent verified
          trainers and nutritionists. We verify the credentials of professionals to the
          best of our ability, but:
        </p>
        <List
          items={[
            "Professionals are independent contractors, not employees of FitFrik.",
            "Any advice, program, diet plan, or service provided by a professional is a direct relationship between you and the professional.",
            "FitFrik is not liable for the accuracy, suitability, or outcome of any third-party advice.",
            "Bookings, sessions, and payments may be facilitated by FitFrik but the underlying service is contracted directly with the professional.",
          ]}
        />
      </Section>

      <Section n="6" title="Acceptable use">
        <p>You agree not to, and not to permit anyone else to:</p>
        <List
          items={[
            "Use the Services for any unlawful, deceptive, discriminatory, or harmful purpose.",
            "Upload content you do not own or have permission to share.",
            "Upload content that is sexually explicit, violent, defamatory, hateful, or that infringes on any third party's rights.",
            "Impersonate any person or entity.",
            "Reverse engineer, decompile, or attempt to extract the source code of the Services.",
            "Interfere with or disrupt the Services, servers, or networks (e.g. DDoS, scraping, spamming).",
            "Use the AI features to generate misleading medical claims or to target other users with harassment.",
            "Circumvent any rate limits, access controls, or technical protections.",
          ]}
        />
        <p>
          We may remove content and suspend accounts that violate this section without
          prior notice.
        </p>
      </Section>

      <Section n="7" title="User content & licence">
        <p>
          You retain all rights in the content you submit to FitFrik (&ldquo;User
          Content&rdquo;), including your food photos, body scans, community posts, and
          messages.
        </p>
        <p>
          By submitting User Content, you grant FitFrik a worldwide, non-exclusive,
          royalty-free, transferable licence to host, store, reproduce, adapt, translate,
          and display the User Content solely to operate, provide, secure, and improve
          the Services, and in accordance with our Privacy Policy. This licence ends when
          you delete the content or delete your account.
        </p>
      </Section>

      <Section n="8" title="Intellectual property">
        <p>
          The Services, including all software, text, graphics, video, workout templates,
          exercise demonstrations, and the FitFrik name and logos, are owned by FitFrik
          Technologies or its licensors and are protected by copyright, trademark, and
          other laws. You are granted a limited, revocable, non-transferable, non-
          exclusive licence to use the Services for personal non-commercial use, subject
          to these Terms.
        </p>
      </Section>

      <Section n="9" title="Third-party services">
        <p>
          The Services integrate with third parties such as Razorpay, Google, Apple
          Health, Google Health Connect, Fitbit, Garmin, Open Food Facts, OpenAI,
          Anthropic, Google AI, ElevenLabs, AWS, Cloudflare, and MongoDB Atlas. Your use
          of those integrations is additionally governed by each provider's own terms and
          privacy policies.
        </p>
      </Section>

      <Section n="10" title="Disclaimers">
        <p>
          The Services are provided on an &ldquo;as is&rdquo; and &ldquo;as
          available&rdquo; basis. To the maximum extent permitted by law, FitFrik
          disclaims all warranties, express or implied, including implied warranties of
          merchantability, fitness for a particular purpose, non-infringement, and
          quiet enjoyment.
        </p>
      </Section>

      <Section n="11" title="Limitation of liability">
        <p>
          To the maximum extent permitted by applicable law, FitFrik, its affiliates, and
          its personnel will not be liable for any indirect, incidental, special,
          consequential, or punitive damages, or any loss of profits, data, goodwill, or
          other intangibles, arising out of or related to your use of the Services.
        </p>
        <p>
          Our total aggregate liability for any claim arising out of or relating to these
          Terms or the Services shall not exceed the greater of (a) the amount you paid
          us in the 12 months preceding the claim, or (b) INR 5,000.
        </p>
        <p>
          Some jurisdictions do not allow the exclusion of certain warranties or the
          limitation of liability for consequential damages. The above limitations apply
          to you only to the extent permitted by law.
        </p>
      </Section>

      <Section n="12" title="Indemnity">
        <p>
          You agree to defend, indemnify, and hold FitFrik harmless from any claim,
          damage, loss, or expense (including reasonable legal fees) arising out of your
          violation of these Terms, your misuse of the Services, or your violation of any
          third-party right.
        </p>
      </Section>

      <Section n="13" title="Termination">
        <List
          items={[
            "You may cancel your subscription or delete your account at any time from Settings.",
            "We may suspend or terminate your access to the Services immediately if you violate these Terms, create risk or legal exposure for us, or if we are required by law.",
            "Upon termination, Sections 6, 7, 8, 10, 11, 12, 14, and 15 will survive.",
          ]}
        />
      </Section>

      <Section n="14" title="Governing law & dispute resolution">
        <p>
          These Terms are governed by the laws of India, without regard to its
          conflict-of-laws principles. Any dispute arising out of or relating to these
          Terms will be subject to the exclusive jurisdiction of the competent courts of
          Bengaluru, Karnataka, India, except where local consumer-protection laws give
          you the right to bring proceedings in your country of residence.
        </p>
        <p>
          Before filing a lawsuit, you agree to first try to resolve the dispute by
          contacting us at{" "}
          <a className="underline" href="mailto:support@fitfrik.com">
            support@fitfrik.com
          </a>
          . We will respond within 15 business days.
        </p>
      </Section>

      <Section n="15" title="IT Act 2000 grievance officer (India)">
        <p>
          In accordance with the Information Technology Act, 2000 and rules made
          thereunder, the grievance officer designated for FitFrik is:
        </p>
        <List
          items={[
            "Name: Grievance Officer, FitFrik Technologies",
            "Email: support@fitfrik.com",
            "Response time: within 15 days of receipt of a written complaint.",
          ]}
        />
      </Section>

      <Section n="16" title="Changes to these Terms">
        <p>
          We may update these Terms from time to time. Material changes will be notified
          in the app or by email at least 14 days before they take effect. Continued use
          after the effective date constitutes acceptance of the revised Terms.
        </p>
      </Section>

      <Section n="17" title="Contact">
        <p>
          FitFrik Technologies — support@fitfrik.com. For postal correspondence,
          please email us to request the current registered office address.
        </p>
      </Section>
    </LegalLayout>
  );
}
